import React, { useEffect, useState } from 'react'
import './ClientFAQ.css';
import PortalHeader from '../clientNavbar/PortalHeader'

// Icons
import { FaPlus, FaMinus } from "react-icons/fa6";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";

// FAQ Interface
interface myFAQItem {
    FAQ_ID: number;
    FAQ_QUESTION: string;
    FAQ_ANSWER: string;
    FAQ_CATEGORY: string;
}


const ClientFAQ = () => {
    const [faqData, setFaqData] = useState<myFAQItem[]>([]);

    // Always scroll to top of component
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);

    // Fetching from the FAQ Django
    const fetchDetectorFAQs = async () => {
        try {
            const response = await fetch('/api/detectordashfaq', {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data: myFAQItem[] = await response.json();
            setFaqData(data); // Assign the fetched gallery data to state
            // console.log(data)

        } catch (err) {
            // Type assertion to 'Error' to access the message property
            if (err instanceof Error) {
                console.error(err.message); // Handle error
            } else {
                console.error('An unknown error occurred'); // Fallback for unknown errors
            }
        }
    };

    useEffect(() => {
        fetchDetectorFAQs()
    }, []); // Empty dependency array to run once on mount

    // FAQ
    const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(new Set());
    const [expandAll, setExpandAll] = useState(false);
    const [closingIndex, setClosingIndex] = useState<number | null>(null);
    const [activeCategory, setActiveCategory] = useState("");

    // Set the initial active category once faqData is loaded
    useEffect(() => {
        if (faqData.length > 0 && !activeCategory) {
            setActiveCategory(faqData[0].FAQ_CATEGORY);
        }
    }, [faqData, activeCategory]);


    const toggleExpand = (index: number) => {
        setExpandAll(false); // Exit expand-all mode

        setExpandedIndexes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                setClosingIndex(index); // Trigger animation
                setTimeout(() => {
                    newSet.delete(index);
                    setExpandedIndexes(new Set(newSet)); // Force update
                    setClosingIndex(null);
                }, 300);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    function closingExpandableWhenSwappingTabs(category: string) {
        setExpandedIndexes(new Set());
        setClosingIndex(null);
        setExpandAll(false);
        setActiveCategory(category);
    }

    // Functions for only showing 2 options of the mobile
    const [menuPage, setMenuPage] = useState(0);
    const itemsPerPage = 2;

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 690);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 690);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const uniqueCategories = Array.from(new Set(faqData.map(faq => faq.FAQ_CATEGORY)));
    const totalPages = Math.ceil(uniqueCategories.length / itemsPerPage);

    // Get only 2 categories for current page
    const visibleCategories = uniqueCategories.slice(
        menuPage * itemsPerPage,
        (menuPage + 1) * itemsPerPage
    );

    const replaceKeywordsWithLinks = (text: string): string => {
        const replacements: { [key: string]: string } = {
            "FNN Contact": `<a href="/fnn_portal_representative" target="_blank" rel="noopener noreferrer">FNN Contact</a>`,
            "ESRI Dashboard": `<a href="/fnn_portal_esri" target="_blank" rel="noopener noreferrer">ESRI Dashboard</a>`,
            "My Account": `<a href="/fnn_portal_profile" target="_blank" rel="noopener noreferrer">My Account</a>`,
            "Tutorial Videos": `<a href="/fnn_portal_video_hub" target="_blank" rel="noopener noreferrer">Tutorial Videos</a>`,
        };

        let result = text;
        for (const [keyword, link] of Object.entries(replacements)) {
            // Use a RegExp with global flag to replace all occurrences
            result = result.replace(new RegExp(`\\b${keyword}\\b`, 'g'), link);
        }

        return result;
    };



    return (
        <div>
            <PortalHeader />

            <div className='faq-and-image'>

                <div className='faq-title-and-icon'>
                    <h1><strong> We are Here to Help</strong></h1>
                    <p>Find quick answers to common questions about your plan, features, and support.</p>
                </div>

                {/* Generate menu dynamically from faqData */}
                <div className="faq-menu">
                    {isMobile ? (
                        <>
                            <span
                                className="faq-arrow"
                                onClick={() => setMenuPage((menuPage + 1) % totalPages)} // Toggle forward
                            >
                                <IoIosArrowBack />
                            </span>

                            {visibleCategories.map((category, idx) => (
                                <p
                                    key={idx}
                                    onClick={() => closingExpandableWhenSwappingTabs(category)}
                                    className={activeCategory === category ? "active" : ""}
                                >
                                    {category}
                                </p>
                            ))}

                            <span
                                className="faq-arrow"
                                onClick={() => setMenuPage((menuPage + 1) % totalPages)} // Toggle forward
                            >
                                <IoIosArrowForward />
                            </span>
                        </>
                    ) : (
                        // Desktop: show all categories
                        uniqueCategories.map((category, idx) => (
                            <p
                                key={idx}
                                onClick={() => closingExpandableWhenSwappingTabs(category)}
                                className={activeCategory === category ? "active" : ""}
                            >
                                {category}
                            </p>
                        ))
                    )}

                </div>



                <div className='faq-wrapper'>
                    <h2>FAQ&apos;s</h2>
                    <h1 className='faq-section-and-buttons'>
                        <strong>{activeCategory}</strong>
                        <div className='faq-adjust-buttons plus-faq'>
                            <button onClick={() => {
                                const allIndexes = faqData
                                    .filter(faq => faq.FAQ_CATEGORY === activeCategory)
                                    .map((_, idx) => idx);
                                setExpandedIndexes(new Set(allIndexes));
                                setClosingIndex(null);
                                setExpandAll(true);
                            }}>
                                Expand All
                            </button>

                            <button onClick={() => {
                                setExpandedIndexes(new Set());
                                setClosingIndex(null);
                                setExpandAll(false);
                            }}>
                                Close All
                            </button>
                        </div>


                    </h1>

                    {/* Filter FAQs by selected category */}
                    {faqData
                        .filter(faq => faq.FAQ_CATEGORY === activeCategory)
                        .map((faq, index) => (
                            <div key={index} className="faq-item">
                                <div className='icon-plus-question' onClick={() => toggleExpand(index)}>
                                    <div className='faq-index-and-question'>
                                        <h1 style={{ color: '#28aae1' }} >0{index + 1} </h1>
                                        <h1><strong>{faq.FAQ_QUESTION}</strong></h1>
                                    </div>
                                    <div className={`faq-icon-wrapper ${expandedIndexes.has(index) || expandAll ? 'expanded' : ''}`}>
                                        <FaPlus className='icon plus' />
                                        <FaMinus className='icon minus' />
                                    </div>
                                </div>

                                {(expandedIndexes.has(index) || closingIndex === index) && (
                                    <div className={`expandable ${closingIndex === index ? 'fade-out' : 'fade-in'}`}>
                                        <p dangerouslySetInnerHTML={{ __html: replaceKeywordsWithLinks(faq.FAQ_ANSWER) }} />
                                    </div>
                                )}

                            </div>
                        ))}
                </div>

            </div>

        </div>
    )
}

export default ClientFAQ